/**
 * Turn Platform - Main Application Router
 * 
 * Routes (Feb 2026 Simplified Structure):
 * - / - Parent Home (action-focused with sidebar nav)
 * - /schedule - Scheduled lessons (Join/Cancel)
 * - /read - Reading Hub (Scan & Read + Storybooks)
 * - /learn - Learning activities (cards, enrichment)
 * - /settings - Account settings + billing
 * - /messages - Support messages
 * - /admin/* - Admin console
 * - /login - Authentication
 * - /trial - Public trial request form (unauthenticated)
 * 
 * ARCHIVED (hidden, code preserved):
 * - /my-turn - Old dashboard (split into /schedule, /settings)
 * - /storybooks/* - Direct storybook access (kept for deep links)
 * - /flashcards/* - Direct flashcard access (redirects to /)
 */

import React from 'react';
import { Routes, Route, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { Login } from './components/auth/Login';
import { PasswordChangePrompt } from './components/auth/PasswordChangePrompt';
import { AdminRouter } from './app/admin';
import StorybookPortal from './portals/storybook/StorybookPortal';
import { AppErrorBoundary } from './components/admin/ErrorBoundary';

// Scan Agent routes
import { ScanAgentLayout } from './app/scan-agent/layout';
import { ScanAgentQueue } from './app/scan-agent/index';
import { ScanAgentScanSession } from './app/scan-agent/scan';
import { ScanAgentEditPage } from './app/scan-agent/edit';
import { ScanAgentNewBook } from './app/scan-agent/new';
import { ScanAgentLibrary } from './app/scan-agent/library';
import { BookstoreBooks } from './app/scan-agent/bookstore';
import { LibraryLookup } from './app/scan-agent/library-lookup';
import { AppLayout } from './components/layout/AppLayout';
import VoiceAgentPage from './app/library/voice';
import { Loader2, Shield } from 'lucide-react';
import { TranslationProvider } from './lib/i18n';

// Admin Preview - ViewAs context for admin parent preview mode
import { ViewAsProvider } from './contexts/ViewAsContext';

// Shared child selection context for multi-child families
import { SelectedChildProvider } from './contexts/ChildContext';

// New simplified pages
import { SchedulePage } from './app/schedule';

import { SettingsPage } from './app/settings';
import { TrialRequestPage } from './app/trial';
import { WelcomePage } from './app/welcome';
import { ConsonantPracticePage } from './app/consonant-practice';
import { TutorPage } from './app/tutor';
import { ResetPasswordPage } from './app/reset-password';
import { BookPage } from './app/book';

// Reading Hub - Scan & Read + Storybooks
import { ReadingHub } from './app/read';

// Book Reader - displays scanned books with Turn Color
import { ScannedBookReader } from './app/library/book/[id]';

// Flashcard Session - vocabulary practice for scanned books
import { FlashcardSession } from './app/library/book/flashcards';

// ARCHIVED: These imports are preserved but routes are hidden
// import { MyTurnDashboard } from './app/my-turn';

// Loading component
const LoadingScreen: React.FC = () => (
  <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center">
    <div className="text-center">
      <Loader2 className="w-12 h-12 animate-spin text-[#5C2D91] mx-auto mb-4" />
      <p className="text-[#636366]">Loading...</p>
    </div>
  </div>
);

// Protected Route component
interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAdmin = false }) => {
  const { user, loading, profileLoaded, isAdmin, isParent, profile } = useAuth();
  const location = useLocation();

  // Show loading while auth state or profile is being fetched
  if (loading || (user && !profileLoaded)) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // User exists but no profile after loading completed - redirect to login
  // This handles stale sessions or users without profiles
  if (!profile) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check for password change requirement
  if (profile?.password_change_required) {
    return <PasswordChangePrompt onComplete={() => { window.location.href = window.location.pathname; }} />;
  }

  // Subscription gate: parents who haven't completed onboarding → redirect to welcome pricing
  const ONBOARDING_EXEMPT_PATHS = ['/welcome', '/settings', '/messages', '/login', '/trial', '/reset-password'];
  const needsOnboarding = profile?.is_parent
    && !profile?.admin_level
    && !profile?.onboarding_complete
    && !ONBOARDING_EXEMPT_PATHS.some(p => location.pathname.startsWith(p));

  if (needsOnboarding) {
    const welcomeToken = profile?.welcome_token;
    if (welcomeToken) {
      return <Navigate to={`/welcome/${welcomeToken}?step=pricing`} replace />;
    }
    return <Navigate to="/settings" replace />;
  }

  // Admin check
  if (requireAdmin && !isAdmin()) {
    if (isParent()) {
      return <Navigate to="/" replace />;
    }
    
    return (
      <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-[#FF3B30]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-[#FF3B30]" />
          </div>
          <h2 className="text-xl font-bold text-[#1C1C1E] mb-2">Access Denied</h2>
          <p className="text-[#636366] mb-6">
            You don't have permission to access this area.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#5C2D91] text-white rounded-xl font-medium hover:bg-[#5C2D91]/80 transition-colors"
          >
            Go Home
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

// Legacy book URL redirect - preserves book ID and query params in new route structure
const LegacyBookRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const query = searchParams.toString();
  return <Navigate to={`/read/book/${id}${query ? `?${query}` : ''}`} replace />;
};

// Smart redirect based on user role
const RoleBasedRedirect: React.FC = () => {
  const { user, loading, profileLoaded, isAdmin, isScanningAgent, profile } = useAuth();

  if (loading || (user && !profileLoaded)) {
    return <LoadingScreen />;
  }

  if (!user || !profile) {
    return <Navigate to="/login" replace />;
  }

  // Admins go to admin dashboard
  if (isAdmin()) {
    return <Navigate to="/admin" replace />;
  }

  // Scanning agents go to their work queue
  if (isScanningAgent()) {
    return <Navigate to="/scan-agent" replace />;
  }

  // Parents stay on home (Voice Agent)
  return null;
};

// Home Page - Parent home with sidebar navigation
const HomePage: React.FC = () => {
  const { user, loading, profileLoaded, isAdmin, isScanningAgent, profile } = useAuth();

  if (loading || (user && !profileLoaded)) {
    return <LoadingScreen />;
  }

  // Admins redirect to admin dashboard (unless previewing as parent)
  const viewAsParam = new URLSearchParams(window.location.search).get('viewAs');
  if (isAdmin() && user && profile && !viewAsParam) {
    return <Navigate to="/admin" replace />;
  }

  // Scanning agents redirect to their work queue (not the parent portal)
  if (isScanningAgent() && user && profile) {
    return <Navigate to="/scan-agent" replace />;
  }

  return (
    <ProtectedRoute>
      <AppLayout>
        <VoiceAgentPage />
      </AppLayout>
    </ProtectedRoute>
  );
};

// Parent Portal Wrapper - ViewAs context for admin preview
const ParentPortalWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ViewAsProvider>
      <SelectedChildProvider>
        {children}
      </SelectedChildProvider>
    </ViewAsProvider>
  );
};

export default function App() {
  const { loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <TranslationProvider>
    <ParentPortalWrapper>
    <Routes>
      {/* ============================================
          PUBLIC ROUTES (Unauthenticated)
          ============================================ */}
      
      {/* Login */}
      <Route path="/login" element={<Login />} />
      
      {/* Password Reset */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* /register redirects to /trial — self-service registration is disabled */}
      <Route path="/register" element={<Navigate to="/trial" replace />} />
      
      {/* Trial Request Form - public facing (lead gen) */}
      <Route path="/trial" element={<TrialRequestPage />} />

      {/* Booking Page - public facing ($200 one-time session) */}
      <Route path="/book" element={<BookPage />} />
      
      {/* Welcome Page - personalized onboarding for offered parents */}
      <Route path="/welcome/:token" element={<WelcomePage />} />
      
      {/* ============================================
          PARENT ROUTES (Feb 2026 Simplified)
          ============================================ */}
      
      {/* Home - Voice Agent */}
      <Route path="/" element={<HomePage />} />

      {/* Schedule - View/manage scheduled lessons */}
      <Route path="/schedule" element={
        <ProtectedRoute>
          <AppErrorBoundary>
          <AppLayout>
            <SchedulePage />
          </AppLayout>
          </AppErrorBoundary>
        </ProtectedRoute>
      } />

      {/* Learn - archived, redirect to home */}
      <Route path="/learn" element={<Navigate to="/" replace />} />

      {/* Consonant Practice */}
      <Route path="/consonant-practice" element={
        <ProtectedRoute>
          <AppLayout>
            <AppErrorBoundary>
              <ConsonantPracticePage />
            </AppErrorBoundary>
          </AppLayout>
        </ProtectedRoute>
      } />

      {/* Tutor - Voice-based literacy tutor (dark theme exception) */}
      <Route path="/tutor" element={
        <ProtectedRoute>
          <AppErrorBoundary>
            <TutorPage />
          </AppErrorBoundary>
        </ProtectedRoute>
      } />

      {/* Read - Book library + request flow */}
      <Route path="/read" element={
        <ProtectedRoute>
          <AppErrorBoundary>
          <AppLayout>
            <ReadingHub />
          </AppLayout>
          </AppErrorBoundary>
        </ProtectedRoute>
      } />

      {/* Read Book - Individual book reader with Turn Color */}
      <Route path="/read/book/:id" element={
        <ProtectedRoute>
          <AppErrorBoundary>
          <ScannedBookReader />
          </AppErrorBoundary>
        </ProtectedRoute>
      } />

      {/* Flashcard Session - vocabulary practice for a scanned book */}
      <Route path="/library/book/:id/flashcards" element={
        <ProtectedRoute>
          <AppErrorBoundary>
          <FlashcardSession />
          </AppErrorBoundary>
        </ProtectedRoute>
      } />

      {/* Settings - Account + Billing */}
      <Route path="/settings" element={
        <ProtectedRoute>
          <AppErrorBoundary>
          <AppLayout>
            <SettingsPage />
          </AppLayout>
          </AppErrorBoundary>
        </ProtectedRoute>
      } />

      {/* ============================================
          ADMIN ROUTES
          ============================================ */}
      
      <Route path="/admin/*" element={
        <ProtectedRoute requireAdmin={true}>
          <AppErrorBoundary>
          <AdminRouter />
          </AppErrorBoundary>
        </ProtectedRoute>
      } />
      
      {/* ============================================
          SCAN AGENT ROUTES
          Access: scanning_agent OR admin roles
          ============================================ */}
      
      <Route path="/scan-agent" element={<AppErrorBoundary><ScanAgentLayout /></AppErrorBoundary>}>
        <Route index element={<ScanAgentQueue />} />
        <Route path="new" element={<ScanAgentNewBook />} />
        <Route path="library" element={<ScanAgentLibrary />} />
        <Route path="bookstore" element={<BookstoreBooks />} />
        <Route path="library-lookup" element={<LibraryLookup />} />
        {/* IMPORTANT: More specific route must come FIRST to avoid collision */}
        <Route path="scan/book/:bookId" element={<ScanAgentScanSession />} />
        <Route path="scan/request/:requestId" element={<ScanAgentScanSession />} />
        <Route path="scan" element={<Navigate to="/scan-agent" replace />} />
        <Route path="edit/:bookId" element={<ScanAgentEditPage />} />
        <Route path="edit" element={<Navigate to="/scan-agent" replace />} />
      </Route>
      
      {/* ============================================
          ARCHIVED ROUTES (Hidden but preserved)
          Storybooks/Flashcards kept for deep links
          ============================================ */}
      
      {/* Storybooks Portal - hidden from nav, kept for deep links */}
      <Route path="/storybooks/*" element={<AppErrorBoundary><StorybookPortal /></AppErrorBoundary>} />

      {/* Flashcards Portal - ARCHIVED: redirect to home */}
      <Route path="/flashcards/*" element={<Navigate to="/" replace />} />
      
      {/* ============================================
          LEGACY REDIRECTS
          Old routes redirect to new structure
          ============================================ */}
      
      <Route path="/library/voice" element={<Navigate to="/" replace />} />
      <Route path="/library/scan" element={<Navigate to="/read" replace />} />
      <Route path="/library/progress" element={<Navigate to="/settings" replace />} />
      <Route path="/library/account" element={<Navigate to="/settings" replace />} />
      {/* Legacy book URLs redirect to new path structure */}
      <Route path="/library/book/:id" element={<LegacyBookRedirect />} />
      <Route path="/library" element={<Navigate to="/" replace />} />
      <Route path="/my-turn" element={<Navigate to="/schedule" replace />} />
      
      {/* Catch-all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </ParentPortalWrapper>
    </TranslationProvider>
  );
}
